import { memo, useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import { getPcmTestimonialForOfferId } from 'constants/pcmTestimonials'
import Image from 'components/Common/Image'
import Sheet from 'components/utils/Sheet'

const ExpertImageContainer = styled.div`
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: ${props => props.theme.borderRadius.round};
  overflow: hidden;
`

interface Props {
  offerId: string
  className?: string
}

function OfferPagePcmTestimonial({ offerId, className }: Props) {
  const intl = useIntl()
  const [isExpanded, setIsExpanded] = useState(false)
  const testimonial = getPcmTestimonialForOfferId(offerId)

  const expandReview = useCallback(() => {
    setIsExpanded(true)
  }, [])

  if (!testimonial) {
    return null
  }

  return (
    <Sheet className={className} background="light-background" padding={20} borderRadius="M">
      <Group direction="vertical" gap={12}>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'VQEsYv4X2AuAvjOKLH4C',
            defaultMessage: 'Our travel expert’s take',
            description: 'Heading for a testimonial section from a travel expert on the offer page.',
          })}
        </Heading>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <ExpertImageContainer>
            <Image
              id={testimonial.imageId}
              alt={testimonial.name}
              fit="center"
              objectFit="cover"
            />
          </ExpertImageContainer>
          <div>
            <BodyText variant="medium">
              {testimonial.name}
            </BodyText>
            <Caption variant="medium" colour="neutral-three">
              {intl.formatMessage({
                id: 'fvyh0XrVxFdjZYEYsqaT',
                defaultMessage: 'Product Contracting Manager, Luxury Escapes',
                description: 'Job title for the travel expert shown alongside their offer page testimonial.',
              })}
            </Caption>
          </div>
        </Group>
        <BodyText variant="medium" lineClamp={isExpanded ? undefined : 3}>
          {intl.formatMessage({
            id: 'PNt2neMc0D5V0ZsRlLqn',
            defaultMessage: '“{review}”',
            description: 'Review text shown in the offer page travel expert testimonial section.',
          }, { review: testimonial.review })}
        </BodyText>
        {!isExpanded && (
          <TextLink size="small" onClick={expandReview}>
            {intl.formatMessage({
              id: 'C0wl3C75CDsHK5k2Vwp2',
              defaultMessage: 'Read more',
              description: 'Button text that expands a clamped travel expert testimonial on the offer page.',
            })}
          </TextLink>
        )}
      </Group>
    </Sheet>
  )
}

export default memo(OfferPagePcmTestimonial)
